import { ChartEmptyState } from "@/components/states";
import { formatNumber } from "@/lib/format";

const DAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function hourLabel(hour: number): string {
  if (hour === 0) return "12AM";
  if (hour < 12) return `${hour}AM`;
  if (hour === 12) return "12PM";
  return `${hour - 12}PM`;
}

/**
 * Day-of-week x hour-of-day heatmap of units sold. Sequential single-hue
 * scale (brand purple into the chart surface); every cell prints its value,
 * so the grid is simultaneously the table view. Intensity uses a square-root
 * scale so one outlier cell doesn't wash out the rest of the week.
 */
export function TrafficHeatmap({
  cells,
}: {
  cells: { dayOfWeek: number; hour: number; units: number }[];
}) {
  const active = cells.filter((c) => c.units > 0);
  if (active.length === 0) {
    return <ChartEmptyState />;
  }

  const byKey = new Map(
    cells.map((c) => [`${c.dayOfWeek}:${c.hour}`, c.units]),
  );
  const minHour = Math.min(...active.map((c) => c.hour));
  const maxHour = Math.max(...active.map((c) => c.hour));
  const maxUnits = Math.max(...active.map((c) => c.units));
  const hours = Array.from(
    { length: maxHour - minHour + 1 },
    (_, i) => minHour + i,
  );

  // Anything > 0 gets at least a visible tint; zero cells stay bare surface.
  const mixPercent = (units: number) =>
    8 + 77 * Math.sqrt(units / maxUnits);

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table
          className="w-full text-xs [font-variant-numeric:tabular-nums]"
          style={{ borderCollapse: "separate", borderSpacing: 2 }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                className="px-2 py-1 text-left font-medium text-muted-foreground"
              >
                Hour
              </th>
              {DAY_LABELS.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className="px-2 py-1 text-right font-medium text-muted-foreground"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map((hour) => (
              <tr key={hour}>
                <th
                  scope="row"
                  className="whitespace-nowrap px-2 py-1 text-left font-normal text-muted-foreground"
                >
                  {hourLabel(hour)}
                </th>
                {DAY_LABELS.map((day, i) => {
                  const units = byKey.get(`${i + 1}:${hour}`) ?? 0;
                  const empty = units === 0;
                  const mix = empty ? 0 : mixPercent(units);
                  return (
                    <td
                      key={day}
                      title={`${day} ${hourLabel(hour)} — ${formatNumber(units)} units`}
                      className="rounded-sm px-2 py-1 text-right transition-shadow hover:shadow-[inset_0_0_0_1.5px_var(--viz-ink)]"
                      style={{
                        background: empty
                          ? "var(--viz-surface)"
                          : `color-mix(in oklab, var(--viz-series-1) ${mix}%, var(--viz-surface))`,
                        color: empty
                          ? "var(--viz-muted)"
                          : mix > 50
                            ? "var(--viz-surface)"
                            : "var(--viz-ink)",
                      }}
                    >
                      {formatNumber(units)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 self-end text-xs text-muted-foreground">
        <span>Fewer</span>
        <div
          aria-hidden
          className="h-2 w-24 rounded-full"
          style={{
            background: `linear-gradient(to right,
              color-mix(in oklab, var(--viz-series-1) 8%, var(--viz-surface)),
              color-mix(in oklab, var(--viz-series-1) 85%, var(--viz-surface)))`,
          }}
        />
        <span>More units</span>
      </div>
    </div>
  );
}
